// infcode_change - new file
import fs from "fs/promises"
import os from "os"
import path from "path"
import * as Log from "@opencode-ai/core/util/log"
import { GlobalBus } from "@/bus/global"
import { Event as SkillEvent } from "@/infcode/skill/shared"
import { AgentMigration, type AgentMigrationReport } from "./agent"
import { KnowledgeMigration, type KnowledgeMigrationReport } from "./knowledge"
import { McpMigration, type McpMigrationReport } from "./mcp"
import { RulesMigration, type RulesMigrationReport } from "./rules"
import { SessionsMigration } from "./sessions"
import { SkillsMigration, type SkillsMigrationReport } from "./skills"
import type { ModuleState, SessionMigrationReport } from "./types"
import { errorMessage, exists, isRecord, readJsonSafe, writeJson } from "./util"

const LOG_PREFIX = "[infcode:migration]"
const log = Log.create({ service: LOG_PREFIX })

const VERSION = "v1-to-v2"

type RulesSummary = RulesMigrationReport["summary"]
type SessionsSummary = SessionMigrationReport["summary"]
type McpSummary = McpMigrationReport["summary"]
type AgentSummary = AgentMigrationReport["summary"]
type SkillsSummary = SkillsMigrationReport["summary"]
type KnowledgeSummary = KnowledgeMigrationReport["summary"]

type ModuleName = "rules" | "sessions" | "mcp" | "agent" | "skills" | "knowledge"

type CompletedMigrationReport = {
  status: "completed"
  version: string
  migratedAt: string
  modules: {
    rules: RulesMigrationReport
    sessions: SessionMigrationReport
    mcp: McpMigrationReport
    agent: AgentMigrationReport
    skills: SkillsMigrationReport
    knowledge: KnowledgeMigrationReport
  }
}

type SkippedMigrationReport = {
  status: "skipped"
  reason: "modules-complete"
  markerPath: string
}

type MigrationResult = CompletedMigrationReport | SkippedMigrationReport

type MarkerFile = {
  version?: string
  migratedAt?: string
  modules?: {
    rules?: ModuleState<RulesSummary>
    sessions?: ModuleState<SessionsSummary>
    mcp?: ModuleState<McpSummary>
    agent?: ModuleState<AgentSummary>
    skills?: ModuleState<SkillsSummary>
    knowledge?: ModuleState<KnowledgeSummary>
  }
}

type ReportFile = Partial<CompletedMigrationReport> & {
  modules?: {
    rules?: RulesMigrationReport
    sessions?: SessionMigrationReport
    mcp?: McpMigrationReport
    agent?: AgentMigrationReport
    skills?: SkillsMigrationReport
    knowledge?: KnowledgeMigrationReport
  }
}

export namespace InfcodeV1Migration {
  export type Result = MigrationResult
  export type RulesReport = RulesMigrationReport
  export type SessionsReport = SessionMigrationReport
  export type McpReport = McpMigrationReport
  export type AgentReport = AgentMigrationReport
  export type SkillsReport = SkillsMigrationReport
  export type KnowledgeReport = KnowledgeMigrationReport

  const backgroundHomes = new Set<string>()

  export function startInBackground(input: { home?: string; projectDir?: string; projectId?: string } = {}): void {
    const home = path.resolve(input.home ?? os.homedir())
    const projectDir = input.projectDir ? path.resolve(input.projectDir) : undefined
    const key = `${home}\0${projectDir ?? ""}`
    if (backgroundHomes.has(key)) return
    backgroundHomes.add(key)
    void run({ home, projectDir, projectId: input.projectId }).catch((err) => {
      log.warn("v1 migration background task failed", { home, error: errorMessage(err) })
    })
  }

  export async function run(input: { home?: string; projectDir?: string; projectId?: string } = {}): Promise<MigrationResult> {
    const home = input.home ?? os.homedir()
    const projectDir = input.projectDir ? path.resolve(input.projectDir) : undefined
    const infcodeRoot = path.join(home, ".infcode")
    const markerPath = path.join(infcodeRoot, ".migration-v1-done")
    const reportPath = path.join(infcodeRoot, "migration-report.json")
    const migratedAt = new Date().toISOString()
    const [marker, previousReport] = await Promise.all([readMarker(markerPath), readReport(reportPath)])
    const rulesComplete = moduleCompleted(marker, "rules")
    const sessionsComplete = moduleCompleted(marker, "sessions")
    const mcpComplete = moduleCompleted(marker, "mcp")
    const agentComplete = moduleCompleted(marker, "agent")
    const skillsComplete = moduleCompleted(marker, "skills")
    const knowledgeComplete = moduleCompleted(marker, "knowledge")

    if (rulesComplete && sessionsComplete && mcpComplete && agentComplete && skillsComplete && knowledgeComplete) {
      log.info("v1 migration modules already complete, skipping", { markerPath })
      return { status: "skipped", reason: "modules-complete", markerPath }
    }

    const rules = rulesComplete
      ? previousReport.modules?.rules ?? reportFromSummary(marker.modules?.rules?.summary)
      : await RulesMigration.run({ home })
    const sessions = sessionsComplete
      ? previousReport.modules?.sessions ?? emptySessionsReport(marker.modules?.sessions?.summary)
      : await SessionsMigration.run({ home })
    const mcp = mcpComplete
      ? previousReport.modules?.mcp ?? emptyMcpReport(marker.modules?.mcp?.summary)
      : await McpMigration.run({ home, projectDir, projectId: input.projectId })
    const agent = agentComplete
      ? previousReport.modules?.agent ?? emptyAgentReport(marker.modules?.agent?.summary)
      : await AgentMigration.run({ home })
    const skills = skillsComplete
      ? previousReport.modules?.skills ?? emptySkillsReport(marker.modules?.skills?.summary)
      : await SkillsMigration.run({ home })
    const knowledge = knowledgeComplete
      ? previousReport.modules?.knowledge ?? emptyKnowledgeReport(marker.modules?.knowledge?.summary)
      : await KnowledgeMigration.run({ home })

    const report: CompletedMigrationReport = {
      status: "completed",
      version: VERSION,
      migratedAt,
      modules: { rules, sessions, mcp, agent, skills, knowledge },
    }

    await fs.mkdir(infcodeRoot, { recursive: true })
    await writeJson(reportPath, report)
    await writeJson(markerPath, {
      version: VERSION,
      migratedAt,
      modules: {
        rules: moduleState(rules.summary),
        sessions: moduleState(sessions.summary),
        mcp: moduleState(mcp.summary),
        agent: moduleState(agent.summary),
        skills: moduleState(skills.summary),
        knowledge: moduleState(knowledge.summary),
      },
    })
    log.info("v1 migration completed", {
      rules: rules.summary,
      sessions: sessions.summary,
      mcp: mcp.summary,
      agent: agent.summary,
      skills: skills.summary,
      knowledge: knowledge.summary,
    })
    if (!skillsComplete) publishSkillChangedAfterMigration(skills)
    if (!agentComplete) publishConfigChangedAfterMigration(agent)
    return report
  }
}

async function readMarker(markerPath: string): Promise<MarkerFile> {
  if (!(await exists(markerPath))) return {}
  const parsed = await readJsonSafe<MarkerFile>(markerPath)
  if (!parsed.ok) {
    log.warn("failed to read v1 migration marker, rerunning modules", { markerPath, error: parsed.message })
    return {}
  }
  return isRecord(parsed.value) ? (parsed.value as MarkerFile) : {}
}

async function readReport(reportPath: string): Promise<ReportFile> {
  if (!(await exists(reportPath))) return {}
  const parsed = await readJsonSafe<ReportFile>(reportPath)
  if (!parsed.ok) {
    log.warn("failed to read v1 migration report, preserving marker state only", { reportPath, error: parsed.message })
    return {}
  }
  return isRecord(parsed.value) ? (parsed.value as ReportFile) : {}
}

function moduleCompleted<T extends ModuleName>(marker: MarkerFile, name: T) {
  return marker.modules?.[name]?.status === "completed"
}

function moduleHasFailures(summary: { failed?: number; documentsFailed?: number }) {
  return (summary.failed ?? 0) > 0 || (summary.documentsFailed ?? 0) > 0
}

function moduleHasSuccess(summary: {
  migrated?: number
  rewritten?: number
  skipped?: number
  documentsMigrated?: number
}) {
  return (
    (summary.migrated ?? 0) > 0 ||
    (summary.rewritten ?? 0) > 0 ||
    (summary.skipped ?? 0) > 0 ||
    (summary.documentsMigrated ?? 0) > 0
  )
}

function moduleState<
  TSummary extends {
    failed?: number
    documentsFailed?: number
    migrated?: number
    rewritten?: number
    skipped?: number
    documentsMigrated?: number
  },
>(summary: TSummary): ModuleState<TSummary> {
  // Best-effort strategy: mark as "completed" if there is any success, even with partial failures
  // Only mark as "failed" if there are failures and zero successes
  const hasFailures = moduleHasFailures(summary)
  const hasSuccess = moduleHasSuccess(summary)
  return {
    status: hasFailures && !hasSuccess ? "failed" : "completed",
    summary,
  }
}

function reportFromSummary(summary: RulesSummary | undefined): RulesMigrationReport {
  return {
    summary: summary ?? { migrated: 0, rewritten: 0, skipped: 0, failed: 0 },
    items: [],
  }
}

function emptySessionsReport(summary: SessionsSummary | undefined): SessionMigrationReport {
  return {
    summary:
      summary ??
      {
        migrated: 0,
        skipped: 0,
        failed: 0,
        degraded: 0,
        logSkipped: 0,
      },
    items: [],
  }
}

function emptyMcpReport(summary: McpSummary | undefined): McpMigrationReport {
  return {
    summary: summary ?? { migrated: 0, rewritten: 0, skipped: 0, failed: 0 },
    items: [],
  }
}

function emptyAgentReport(summary: AgentSummary | undefined): AgentMigrationReport {
  return {
    summary: summary ?? { migrated: 0, rewritten: 0, skipped: 0, failed: 0 },
    items: [],
  }
}

function emptySkillsReport(summary: SkillsSummary | undefined): SkillsMigrationReport {
  return {
    summary: summary ?? { migrated: 0, rewritten: 0, skipped: 0, failed: 0 },
    items: [],
  }
}

function emptyKnowledgeReport(summary: KnowledgeSummary | undefined): KnowledgeMigrationReport {
  return {
    summary:
      summary ??
      {
        migrated: 0,
        skipped: 0,
        failed: 0,
        documentsMigrated: 0,
        documentsSkipped: 0,
        documentsFailed: 0,
      },
    items: [],
  }
}

function publishSkillChangedAfterMigration(skills: SkillsMigrationReport): void {
  const projectChanges = new Map<string, string | undefined>()
  let globalChanged = false

  for (const item of skills.items) {
    if (!skillMigrationChangedDisk(item)) continue
    if (item.scope === "global") {
      globalChanged = true
      continue
    }
    if (item.projectDir) projectChanges.set(item.projectDir, item.projectId)
  }

  if (globalChanged) publishSkillChanged("global", "global")
  for (const [directory, projectId] of projectChanges) {
    publishSkillChanged("project", directory, projectId)
  }
}

function skillMigrationChangedDisk(item: SkillsMigrationReport["items"][number]): boolean {
  return item.status === "migrated" || item.status === "rewritten"
}

function publishSkillChanged(scope: "global" | "project", directory: string, project?: string): void {
  GlobalBus.emit("event", {
    directory,
    ...(project ? { project } : {}),
    payload: {
      type: SkillEvent.Changed.type,
      properties: { scope },
    },
  })
}

function publishConfigChangedAfterMigration(agent: AgentMigrationReport): void {
  if (!agentMigrationChangedDisk(agent)) return
  GlobalBus.emit("event", {
    directory: "global",
    payload: {
      type: "global.config.updated",
      properties: {},
    },
  })
}

function agentMigrationChangedDisk(agent: AgentMigrationReport): boolean {
  return agent.summary.migrated > 0 || agent.summary.rewritten > 0
}
